import React, { useEffect, useMemo, useState } from "react";
import { Button, Empty, Input, message, Modal, Select, Space, Table, Tooltip } from "antd";
import { ExclamationCircleOutlined, InfoCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { doc, updateDoc } from "firebase/firestore";
import { firestore } from "../../../../../configs/FirebaseConfig";
import {
  FIRESTORE_PRICE_LISTS_TABLE,
  FIRESTORE_PRODUCTS_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "../../../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../../../constants/CompanyConstants";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import dayjs from "dayjs";
import { formatPrice } from "../../../../../functions/formatters";
import { CURRENCY } from "../../../../../constants/DeliveryConstants";
import utils from "../../../../../utils";
import { uniqueMergeArrays } from "../../../../../functions/arrays";
import { PRODUCT_COMPANY_GROUPS, PRODUCT_GROUPS } from "../../../../../constants/ProductConstant";
import { StyledSelect } from "../../../../../styles/sharedStyles";
import { usePriceLists } from "../../../../../data/hooks/firestore/usePriceLists";
import Index from "../../../../../components/shared-components/EditablePriceInput";
import { getBundlePackagingPrice, recalculateBundleItemPricesForOverride } from "../../../../../functions/pricing";
import { NETHERLANDS_COUNTRY_CODE } from "../../../../../constants/PricingConstants";

const { Option } = Select;

const EditPriceListModal = ({ visible, onClose, priceList, products }) => {
  const { t } = useTranslation();
  const { email } = useAuth();
  const { getPriceListById } = usePriceLists();

  const [searchText, setSearchText] = useState("");
  const [clickedCategory, setClickedCategory] = useState("All");
  const [clickedProductCompany, setClickedProductCompany] = useState("All");
  const [clickedProductGroup, setClickedProductGroup] = useState("All");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [localCustomPrices, setLocalCustomPrices] = useState({});
  const [saving, setSaving] = useState(false);

  // Get real-time priceList data
  const livePriceList = getPriceListById(priceList?.id) || priceList;

  // Include all products (bundles and non-bundles)
  const allDisplayProducts = useMemo(() => {
    if (!products) return [];
    return products;
  }, [products]);

  const categories = uniqueMergeArrays(allDisplayProducts, "productCategory");

  // Reset local state when modal opens
  useEffect(() => {
    if (visible && livePriceList) {
      setLocalCustomPrices({ ...(livePriceList.customPrices || {}) });
      applyFilters(searchText, clickedCategory, clickedProductCompany, clickedProductGroup);
    }
  }, [visible, livePriceList, allDisplayProducts]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    const originalPrices = livePriceList?.customPrices || {};
    const originalKeys = Object.keys(originalPrices).sort();
    const localKeys = Object.keys(localCustomPrices).sort();

    // Different number of keys
    if (originalKeys.length !== localKeys.length) return true;

    // Check if any keys are different
    if (originalKeys.join(",") !== localKeys.join(",")) return true;

    // Check if any values are different
    return originalKeys.some((key) => originalPrices[key] !== localCustomPrices[key]);
  }, [livePriceList, localCustomPrices]);

  const applyFilters = (searchValue, category, productCompany, productGroup) => {
    let filtered = [...allDisplayProducts];

    // Apply category filter
    if (category && category !== "All") {
      filtered = filtered.filter((product) => product.productCategory === category);
    }

    // Apply company filter
    if (productCompany && productCompany !== "All") {
      filtered = filtered.filter((product) => product.company === productCompany);
    }

    // Apply product group filter
    if (productGroup && productGroup !== "All") {
      filtered = filtered.filter((product) => product.productGroup === productGroup);
    }

    // Apply search filter
    if (searchValue) {
      filtered = filtered.filter(
        (product) =>
          product.productId.toLowerCase().includes(searchValue.toLowerCase()) ||
          (product.productName && product.productName.toLowerCase().includes(searchValue.toLowerCase())),
      );
    }

    setFilteredProducts(filtered);
  };

  const onSearch = (value) => {
    setSearchText(value);
    applyFilters(value, clickedCategory, clickedProductCompany, clickedProductGroup);
  };

  const handleShowCategory = (value) => {
    setClickedCategory(value);
    applyFilters(searchText, value, clickedProductCompany, clickedProductGroup);
  };

  const handleShowProductCompany = (value) => {
    setClickedProductCompany(value);
    applyFilters(searchText, clickedCategory, value, clickedProductGroup);
  };

  const handleShowProductGroup = (value) => {
    setClickedProductGroup(value);
    applyFilters(searchText, clickedCategory, clickedProductCompany, value);
  };

  const handlePriceChange = (productId, value) => {
    if (!value || value === "") {
      const updatedPrices = { ...localCustomPrices };
      delete updatedPrices[productId];
      setLocalCustomPrices(updatedPrices);
      return;
    }

    const product = allDisplayProducts.find((p) => p.id === productId);
    const standardPrice = parseFloat(product?.price || 0);
    let newPrice = parseFloat(value);

    // For bundles, enforce minimum price = packaging price
    if (product?.isBundle) {
      const packagingPrice = getBundlePackagingPrice(product);
      if (newPrice < packagingPrice) {
        message.warning(
          t("notifications.bundle_price_below_packaging_minimum", { price: formatPrice(packagingPrice) }),
        );
        newPrice = packagingPrice;
      }
    }

    const updatedPrices = { ...localCustomPrices };

    if (newPrice === standardPrice) {
      delete updatedPrices[productId];
    } else {
      updatedPrices[productId] = newPrice;
    }

    setLocalCustomPrices(updatedPrices);
  };

  // The NL country price list is leading: its prices ARE the product's own price, not an
  // override. The product doc still carries a deprecated price field as a last-resort fallback,
  // so changes made here must also be written through to the product itself to keep both in
  // sync. Bundles also redistribute their bundleItems proportionally to the new total, same as
  // a manual override in the product form itself.
  const isNetherlandsList =
    livePriceList?.type === "country" && livePriceList?.countryCode === NETHERLANDS_COUNTRY_CODE;

  const handleSaveAll = async () => {
    try {
      setSaving(true);

      await updateDoc(
        doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_PRICE_LISTS_TABLE, livePriceList.id),
        {
          customPrices: localCustomPrices,
          updatedAt: dayjs().unix(),
          updatedBy: email,
        },
      );

      if (isNetherlandsList) {
        const originalPrices = livePriceList.customPrices || {};
        const changedProductIds = Object.keys(localCustomPrices).filter(
          (productId) => localCustomPrices[productId] !== originalPrices[productId],
        );

        for (const productId of changedProductIds) {
          const product = allDisplayProducts.find((p) => p.id === productId);
          if (!product) continue;

          const newPrice = localCustomPrices[productId];
          const productUpdate = {
            price: newPrice,
            updatedAt: dayjs().unix(),
            updatedBy: email,
          };

          // Bundles: the new price is a total, so the underlying items must be redistributed
          // proportionally as well (packaging stays fixed), same as a manual override in the
          // product form itself.
          if (product.isBundle) {
            productUpdate.bundleItems = recalculateBundleItemPricesForOverride(product, newPrice).map((item) => ({
              productId: item.productId,
              quantity: item.quantity,
              price: parseFloat(parseFloat(item.price).toFixed(2)),
            }));
          }

          await updateDoc(
            doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_PRODUCTS_TABLE, productId),
            productUpdate,
          );
        }
      }

      message.success(t("notifications.price_list_updated"));
      onClose();
    } catch (error) {
      console.error("Error saving prices:", error);
      message.error(t("notifications.error_saving_prices"));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: t("unsaved_changes"),
        icon: <ExclamationCircleOutlined />,
        content: t("unsaved_changes_warning"),
        okText: t("yes"),
        cancelText: t("no"),
        onOk: () => {
          resetFilters();
          onClose();
        },
      });
    } else {
      resetFilters();
      onClose();
    }
  };

  const resetFilters = () => {
    setSearchText("");
    setClickedCategory("All");
    setClickedProductCompany("All");
    setClickedProductGroup("All");
  };

  const tableColumns = [
    {
      title: t("name"),
      dataIndex: "productName",
      key: "productName",
      sorter: (a, b) => utils.antdTableSorter(a, b, "productName"),
      defaultSortOrder: "descend",
      render: (name, record) => (
        <span>
          {name}
          {record.isBundle && (
            <span style={{ marginLeft: 6, fontSize: "11px", color: "#888", fontWeight: "normal" }}>
              ({t("bundle")})
            </span>
          )}
        </span>
      ),
    },
    // For the NL price list the standard price and custom price are always equal (NL is
    // leading), so only a single editable "Price" column is shown instead of two.
    ...(isNetherlandsList
      ? []
      : [
          {
            title: t("standard_price"),
            dataIndex: "price",
            key: "standardPrice",
            width: 140,
            render: (price, record) => {
              if (!record.isBundle) {
                return (
                  <span>
                    {CURRENCY}
                    {formatPrice(price)}
                  </span>
                );
              }
              const packagingPrice = getBundlePackagingPrice(record);
              return (
                <span>
                  {CURRENCY}
                  {formatPrice(price)}
                  {packagingPrice > 0 && (
                    <Tooltip
                      title={`${t("packaging")}: ${CURRENCY}${formatPrice(packagingPrice)} (${t(
                        "bundle_packaging_fixed",
                      )})`}
                    >
                      <InfoCircleOutlined style={{ marginLeft: 6, color: "#888", fontSize: "12px" }} />
                    </Tooltip>
                  )}
                </span>
              );
            },
            sorter: (a, b) => parseFloat(a.price) - parseFloat(b.price),
          },
        ]),
    {
      title: isNetherlandsList ? t("price") : t("custom_price"),
      dataIndex: "customPrice",
      key: "customPrice",
      width: 140,
      render: (_, record) => (
        <Index record={record} localCustomPrices={localCustomPrices} onPriceChange={handlePriceChange} />
      ),
      sorter: (a, b) => {
        const priceA = parseFloat(localCustomPrices[a.id] || a.price);
        const priceB = parseFloat(localCustomPrices[b.id] || b.price);
        return priceA - priceB;
      },
    },
  ];

  if (!livePriceList) return null;

  return (
    <Modal
      title={t("edit_price_list", { name: livePriceList.name })}
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="close" onClick={handleClose}>
          {t("close")}
        </Button>,
        <Button key="save" type="primary" onClick={handleSaveAll} loading={saving} disabled={!hasUnsavedChanges}>
          {t("save")}
        </Button>,
      ]}
      width={800}
      centered
      zIndex={1100}
    >
      {/* Filters */}
      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          style={{ maxWidth: 200 }}
          value={searchText}
          placeholder={t("search")}
          prefix={<SearchOutlined />}
          allowClear
          onChange={(e) => onSearch(e.target.value)}
        />
        <StyledSelect
          className="w-100"
          style={{ minWidth: 140 }}
          $isFiltered={clickedProductCompany !== "All"}
          onChange={handleShowProductCompany}
          placeholder={t("company")}
          value={clickedProductCompany}
        >
          <Option value="All">{t("all_companies")}</Option>
          {PRODUCT_COMPANY_GROUPS.map((group) => (
            <Option key={group.value} value={group.value}>
              {group.label}
            </Option>
          ))}
        </StyledSelect>
        <StyledSelect
          className="w-100"
          style={{ minWidth: 140 }}
          $isFiltered={clickedProductGroup !== "All"}
          onChange={handleShowProductGroup}
          placeholder={t("product_group")}
          value={clickedProductGroup}
        >
          <Option value="All">{t("all_groups")}</Option>
          {PRODUCT_GROUPS.map((group) => (
            <Option key={group.value} value={group.value}>
              {t(group.label)}
            </Option>
          ))}
        </StyledSelect>
        <StyledSelect
          className="w-100"
          style={{ minWidth: 140 }}
          $isFiltered={clickedCategory !== "All"}
          onChange={handleShowCategory}
          placeholder={t("category")}
          value={clickedCategory}
        >
          <Option value="All">{t("all_categories")}</Option>
          {categories.map((category) => (
            <Option key={category} value={category}>
              {category}
            </Option>
          ))}
        </StyledSelect>
      </Space>

      {/* Products Table */}
      <Table
        columns={tableColumns}
        showSorterTooltip={false}
        dataSource={filteredProducts}
        rowKey="id"
        locale={{
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("table_no_products")} />,
        }}
      />
    </Modal>
  );
};

export default EditPriceListModal;
